/* eslint-disable lines-between-class-members */
import * as THREE from "three";
import { logicalChildrenOf, ScriptableMixin, ScriptableObject, ScriptableObjects } from "./ScriptableObjects.js";

const HAS_AFRAME = typeof window !== "undefined" && !!window.AFRAME;
// eslint-disable-next-line no-undef
const ctor = typeof globalThis !== "undefined" && (globalThis.AmbientLightNode || globalThis.AmbientLight);
const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || (typeof THREE !== "undefined" && v instanceof THREE.Object3D)));

const AMBIENT_LIGHT_PROPS = ["color", "intensity"];

class AmbientLightEditorAdapter {
  constructor(sourceObject, originalDescriptors = new Map()) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    AMBIENT_LIGHT_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v); // Store original value in adapter
          const desc = this._originalDescriptors.get(prop);
          if (desc?.set) {
            try {
              desc.set.call(this.sourceObject, v);
            } catch {
              /* ignore */
            }
          } else if (prop === "color" && this.sourceObject?.color?.set) {
            this.sourceObject.color.set(v);
            this._values.set(prop, this.sourceObject.color);
          } else if (prop === "color" && this.sourceObject?.color?.isColor) {
            // Convert hex colors to THREE.Color for editor context
            if (typeof v === "string" || typeof v === "number") {
              this.sourceObject.color.set(v);
            } else if (v?.isColor) {
              this.sourceObject.color.copy(v);
            }
            this._values.set(prop, this.sourceObject.color);
          } else {
            try {
              this.sourceObject[prop] = v;
            } catch {
              /* ignore */
            }
          }
        }
      });

      // seed from source without invoking setters when possible
      const desc = originalDescriptors.get(prop);
      let initial;
      try {
        if (prop === "color" && sourceObject?.color?.isColor) {
          // For color properties, ensure we store the actual THREE.Color object
          initial = sourceObject.color;
        } else if (desc?.get) {
          initial = desc.get.call(sourceObject);
        } else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) {
          initial = sourceObject[prop];
        }
      } catch {
        /* ignore */
      }
      
      this._values.set(prop, initial);
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

class AmbientLightAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;

    const AMBIENT_LIGHT_COMPONENT = "ambient-light";

    const setComponentProp = (component, key, val) => {
      if (!this.sourceObject?.el) return;
      const current = {
        ...(this.sourceObject.el.getAttribute(component) || {})
      };
      current[key] = val;
      this.sourceObject.el.setAttribute(component, current);
    };

    const getComponentProp = (component, key) =>
      this.sourceObject?.el?.getAttribute(component)?.[key];

    AMBIENT_LIGHT_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getComponentProp(AMBIENT_LIGHT_COMPONENT, prop),
        set: v => setComponentProp(AMBIENT_LIGHT_COMPONENT, prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

function collectDescriptorsFor(obj) {
  const map = new Map();
  for (const prop of AMBIENT_LIGHT_PROPS) {
    let cur = obj;
    while (cur) {
      const d = Object.getOwnPropertyDescriptor(cur, prop);
      if (d) {
        map.set(prop, d);
        break;
      }
      cur = Object.getPrototypeOf(cur);
    }
    if (!map.has(prop)) map.set(prop, undefined);
  }
  return map;
}

function ensureAdapter(self) {
  if (self._adapter) return self._adapter;

  // A-Frame path if we have an element
  if (HAS_AFRAME && (self.el || self.__el)) {
    Object.defineProperty(self, "_adapter", { value: new AmbientLightAFrameAdapter(self), configurable: true });
    return self._adapter;
  }

  // Editor / plain Three path
  const descriptors = collectDescriptorsFor(self);
  Object.defineProperty(self, "_adapter", {
    value: new AmbientLightEditorAdapter(self, descriptors),
    configurable: true
  });
  return self._adapter;
}

// DRY helper: upgrade an editor AmbientLightNode to scriptable and attach adapter + proxies
function makeEditorAmbientLightNodeScriptable(node) {
  if (!node) return node;
  if (node.isScriptableObject === true) return node;

  // Preserve original chain and add Scriptable API on top
  const originalProto = Object.getPrototypeOf(node);
  const ScriptableClass = ScriptableMixin((originalProto && originalProto.constructor) || ctor || THREE.Object3D);
  if (Object.getPrototypeOf(ScriptableClass.prototype) !== originalProto) {
    Object.setPrototypeOf(ScriptableClass.prototype, originalProto);
  }
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  // Annotate type
  try {
    if (!node.scriptableType) 
      Object.defineProperty(node, "scriptableType", { value: AmbientLightElement.scriptableType, configurable: true });
  } catch (_) {
    node.scriptableType = AmbientLightElement.scriptableType;
  }

  const descriptors = collectDescriptorsFor(node);
  try {
    Object.defineProperty(node, "_adapter", { value: new AmbientLightEditorAdapter(node, descriptors), configurable: true });
  } catch (_) {
    node._adapter = new AmbientLightEditorAdapter(node, descriptors);
  }

  for (const prop of AMBIENT_LIGHT_PROPS) {
    try {
      Object.defineProperty(node, prop, {
        get() {
          return this._adapter?.[prop];
        },
        set(v) {
          if (this._adapter) this._adapter[prop] = v;
        },
        enumerable: true,
        configurable: true
      });
    } catch (_) {
      /* best-effort */
    }
  }

  // Ensure clones keep scriptable behaviour
  if (!ScriptableClass.prototype.hasOwnProperty("clone")) {
    const originalClone = (originalProto && originalProto.clone) || ctor.prototype.clone;
    ScriptableClass.prototype.clone = function(recursive = true) {
      const cloned = originalClone ? originalClone.call(this, recursive) : THREE.Object3D.prototype.clone.call(this, recursive);
      return makeEditorAmbientLightNodeScriptable(cloned);
    };
  }

  return node;
}



// Editor factory for AmbientLight (editor/plain Three)
function createAmbientLightElementEditor(initial = {}) {
  const Ctor = globalThis.AmbientLightNode || globalThis.AmbientLight;
  if (typeof Ctor !== "function") {
    throw new Error("AmbientLightElementEditor: AmbientLightNode constructor not found.");
  }

  let node;
  try {
    const editor = ScriptableObject.editor || globalThis.editor;
    node = new Ctor(editor);
  } catch (e) {
    throw new Error(`AmbientLightElementEditor: failed to construct – ${e?.message || e}`);
  }

  makeEditorAmbientLightNodeScriptable(node);

  for (const k of Object.keys(initial)) {
    if (k in node) {
      // Handle special cases for initial color values
      if (k === "color" && node.color?.isColor) {
        if (typeof initial[k] === "string" || typeof initial[k] === "number") {
          node.color.set(initial[k]);
        } else if (initial[k]?.isColor) {
          node.color.copy(initial[k]);
        }
      } else {
        node[k] = initial[k];
      }
    }
  }
  
  return node;
}

export class AmbientLightElement extends ScriptableObject {
  static scriptableType = "ambient-light";

  constructor(initial = {}) {
    super();

    // A-Frame creation: build <a-entity>, adopt, and return the shifted object
    if (HAS_AFRAME) {
      const el = document.createElement("a-entity");
      el.setAttribute("ambient-light", {
        color: initial.color || "#404040",
        intensity: initial.intensity !== undefined ? initial.intensity : 1
      });

      const obj = this.constructor.adopt(el);
      if (Object.getPrototypeOf(obj) !== this.constructor.prototype) {
        Object.setPrototypeOf(obj, this.constructor.prototype);
      }

      // lazy init adapter and apply initial props
      ensureAdapter(obj);
      for (const k of Object.keys(initial)) {
        if (k in obj) obj[k] = initial[k];
      }

      return obj; // IMPORTANT: return shifted object
    }

    // Editor / plain Three: construct real AmbientLightNode and upgrade to scriptable
    const node = createAmbientLightElementEditor(initial);
    return node;
  }

  copy(source, recursive = true) {
    if (!source || !source.isScriptableObject) return this;

    // 1) copy transform only (no children)
    THREE.Object3D.prototype.copy.call(this, source, /* recursive = */ false);

    // 2) copy public props via adapter (deep where needed)
    const take = (prop, val) => {
      if (val === undefined) return;
      
      // deep-clone where necessary to avoid shared refs
      if (val?.isColor) {
        this[prop] = val.clone();
      } else if (Array.isArray(val)) {
        this[prop] = val.slice();
      } else {
        this[prop] = val;
      }
    };

    for (const p of AMBIENT_LIGHT_PROPS) {
      take(p, source[p]);
    }

    // 3) clear existing children
    if (this.children?.length) {
      for (const c of [...this.children]) THREE.Object3D.prototype.remove.call(this, c);
    }

    // 4) replicate children via our public API
    if (recursive) {
      for (const child of logicalChildrenOf(source)) {
        this.add(child.clone());
      }
    }

    return this;
  }

  clone(recursive = true) {
    const init = {};

    for (const p of AMBIENT_LIGHT_PROPS) {
      const v = this[p];
      if (v === undefined) continue;
      
      // Follow the same pattern as other implementations
      if (v?.isColor) {
        init[p] = v.clone();
      } else if (Array.isArray(v)) {
        init[p] = v.slice();
      } else {
        init[p] = v;
      }
    }

    const dest = new AmbientLightElement(init);

    // Copy transforms/children; copy() also uses the adapter surface.
    dest.position.copy(this.position);
    dest.quaternion.copy(this.quaternion);
    dest.scale.copy(this.scale);

    return dest.copy(this, recursive);
  }

  // convenience (optional)
  static create(initial = {}) {
    return new this(initial);
  }
}

// If you create objects via adopt(el) (constructor doesn't run),
// the accessor getter calls ensureAdapter(this) and mounts the adapter on first access.
// That's why binding these getters on the prototype is crucial—even for adopted instances.
AMBIENT_LIGHT_PROPS.forEach(prop => {
  Object.defineProperty(AmbientLightElement.prototype, prop, {
    get() {
      return ensureAdapter(this)?.[prop];
    },
    set(v) {
      const a = ensureAdapter(this);
      if (a) a[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

ScriptableObjects.registerType("ambient-light", source => {
  if (!source) return source;

  // Only promote if type matches (brand can live on obj or on el.object3D)
  const type = source.scriptableType || source.object3D?.scriptableType || source?.__el?.object3D?.scriptableType || source?.el?.object3D?.scriptableType;
  if (type !== AmbientLightElement.scriptableType) return source;

  // If given an A-Frame element or a Scriptable with .el -> adopt that element
  if (isAFrameEntity(source)) return AmbientLightElement.adopt(source);

  // Editor/plain-Three: upgrade in-place to scriptable and attach adapter + proxies
  if (isObject3D(source)) return makeEditorAmbientLightNodeScriptable(source);

  return source;
});

export {
  AmbientLightAFrameAdapter,
  AmbientLightEditorAdapter,
  AMBIENT_LIGHT_PROPS
};
